import { Controller } from "@hotwired/stimulus"
import consumer from "../channels/consumer"

// Owns the active discussion thread: ActionCable wiring (new/edit/delete/
// reaction/read/typing), composer with auto-grow + send/typing debouncing,
// soft-delete rendering, "Vu" indicator under the last own message.
export default class extends Controller {
  static targets = ["messages", "input", "form", "sendBtn", "files", "typing", "typingLabel", "parentInput", "clientUuidInput"]
  static values  = { discussionId: Number, currentUserId: Number }

  connect() {
    this._typers = new Map()
    this._typingTimers = new Map()
    this._lastTypingSent = 0
    this._typingStopTimer = null

    this._subscription = consumer.subscriptions.create(
      { channel: "DiscussionChannel", discussion_id: this.discussionIdValue },
      { received: (data) => this.onReceived(data) }
    )

    this._scrollToBottom(false)
    this._markVisibleAsRead()
    this._bindAutoGrow()
  }

  disconnect() {
    this._subscription?.unsubscribe()
    clearTimeout(this._typingStopTimer)
  }

  // ---------------------------------------------------------------------------
  // ActionCable receive
  // ---------------------------------------------------------------------------
  onReceived(data) {
    if (!data) return

    // New message — payload has both rendered HTML and JSON.
    if (data.html && data.json) {
      this._appendMessage(data.html, data.json)
      return
    }

    switch (data.type) {
      case "edit":     return this._handleEdit(data)
      case "delete":   return this._handleDelete(data)
      case "reaction": return this._handleReaction(data)
      case "read":     return this._handleRead(data)
      case "typing":   return this._handleTyping(data)
      default:         return
    }
  }

  _appendMessage(html, json) {
    // Cable echo dedup: if we already have a node for this server id, drop.
    // Also reconcile an optimistic placeholder that was inserted client-side
    // with a temp data-client-uuid attribute.
    if (json.id != null) {
      const existing = this.messagesTarget.querySelector(`[data-message-id="${json.id}"]`)
      if (existing) return
    }
    if (json.client_uuid) {
      const placeholder = this.messagesTarget.querySelector(`[data-client-uuid="${json.client_uuid}"]`)
      if (placeholder) {
        placeholder.dataset.messageId = String(json.id)
        delete placeholder.dataset.clientUuid
        // Refresh content from the new HTML; keeps avatar/group state.
        placeholder.innerHTML = html
        this._applyViewerSide(placeholder, json)
        this._scrollToBottom(true)
        return
      }
    }

    const li = document.createElement("li")
    li.dataset.messageId = String(json.id)
    li.innerHTML = html
    this._applyViewerSide(li, json)
    this._applyGrouping(li, json)
    this.messagesTarget.appendChild(li)
    this._scrollToBottom(true)
    if (json.author?.id !== this.currentUserIdValue) {
      this._markMessageRead(json.id)
    }
  }

  // The server renders the bubble in "neutral" mode (viewer: nil) on cable
  // broadcasts so every recipient gets the same HTML. Here we re-apply the
  // own / not-own class based on THIS client's currentUserId.
  _applyViewerSide(li, json) {
    const root = li.querySelector(".message")
    if (!root) return
    const ownerId = json.author?.id ?? Number(root.dataset.authorId)
    if (ownerId === this.currentUserIdValue) {
      root.classList.add("message--own")
      // Hide the author label + role (only useful for received).
      root.querySelector(".message__author")?.remove()
      // Hide avatar slot.
      const slot = root.querySelector(".message__avatar-slot")
      if (slot) slot.innerHTML = ""
    } else {
      root.classList.remove("message--own")
    }
  }

  // Collapse pill corners if the previous message is from the same author
  // within ~5 minutes. The neutral broadcast renders both flags `true`, so
  // we adjust on insert.
  _applyGrouping(li, json) {
    const prev = li.previousElementSibling
    if (!prev) return
    const prevMsg = prev.querySelector(".message")
    const curMsg  = li.querySelector(".message")
    if (!prevMsg || !curMsg) return
    if (prevMsg.dataset.authorId !== curMsg.dataset.authorId) return

    prevMsg.classList.remove("message--last-in-group")
    prevMsg.classList.add("message--mid-group")
    prevMsg.querySelector(".message__footer")?.remove()
    const prevSlot = prevMsg.querySelector(".message__avatar-slot")
    if (prevSlot) prevSlot.innerHTML = ""

    curMsg.classList.remove("message--first-in-group")
    curMsg.classList.add("message--mid-group")
    curMsg.querySelector(".message__author")?.remove()
  }

  _handleEdit({ message_id, content, edited_at }) {
    const node = this.messagesTarget.querySelector(`[data-message-id="${message_id}"] .message__bubble-content`)
    if (node) node.innerHTML = String(content).replace(/\n/g, "<br>")
    const meta = this.messagesTarget.querySelector(`[data-message-id="${message_id}"] .message__meta`)
    if (meta && !meta.querySelector(".message__edited")) {
      const tag = document.createElement("span")
      tag.className = "message__edited"
      tag.textContent = "modifié"
      meta.appendChild(tag)
    }
  }

  _handleDelete({ message_id }) {
    const root = this.messagesTarget.querySelector(`[data-message-id="${message_id}"] .message`)
    if (!root) return
    root.classList.add("message--deleted")
    const content = root.querySelector(".message__bubble-content")
    if (content) {
      content.innerHTML = "<em>Ce message a été supprimé.</em>"
      content.classList.add("message__bubble-content--deleted")
    }
    root.querySelectorAll(".message__actions, .message__reactions, .message__attachments, .message__reply-quote").forEach((el) => el.remove())
  }

  _handleReaction({ message_id, reactions_summary }) {
    const root = this.messagesTarget.querySelector(`[data-message-id="${message_id}"]`)
    if (!root) return
    let chips = root.querySelector(".message__reactions")
    if (!chips) {
      chips = document.createElement("div")
      chips.className = "message__reactions"
      chips.dataset.reactionsTarget = "chips"
      root.querySelector(".message__bubble")?.appendChild(chips)
    }
    chips.innerHTML = ""
    Object.entries(reactions_summary || {}).forEach(([emoji, userIds]) => {
      const btn = document.createElement("button")
      btn.type = "button"
      btn.className = "message__reaction-chip"
      if (userIds.includes(this.currentUserIdValue)) btn.classList.add("is-own")
      btn.dataset.action = "reactions#toggle"
      btn.dataset.reactionsEmojiParam = emoji
      btn.innerHTML = `<span>${emoji}</span><span class="message__reaction-count">${userIds.length}</span>`
      chips.appendChild(btn)
    })
    if (chips.childElementCount === 0) chips.remove()
  }

  _handleRead({ message_id, user_id }) {
    if (user_id === this.currentUserIdValue) return
    const root = this.messagesTarget.querySelector(`[data-message-id="${message_id}"]`)
    if (!root) return
    if (!root.querySelector(".message__seen")) {
      const tick = document.createElement("span")
      tick.className = "message__seen"
      tick.textContent = "Vu"
      root.querySelector(".message__bubble")?.appendChild(tick)
    }
  }

  _handleTyping({ user_id, name, is_typing }) {
    if (user_id === this.currentUserIdValue) return

    if (is_typing) {
      this._typers.set(user_id, name)
      clearTimeout(this._typingTimers.get(user_id))
      this._typingTimers.set(user_id, setTimeout(() => {
        this._typers.delete(user_id)
        this._renderTyping()
      }, 4000))
    } else {
      this._typers.delete(user_id)
      clearTimeout(this._typingTimers.get(user_id))
    }
    this._renderTyping()
  }

  _renderTyping() {
    if (this._typers.size === 0) {
      this.typingTarget.hidden = true
      return
    }
    const names = Array.from(this._typers.values()).join(", ")
    this.typingLabelTarget.textContent = `${names} ${this._typers.size === 1 ? "est" : "sont"} en train d'écrire`
    this.typingTarget.hidden = false
  }

  // ---------------------------------------------------------------------------
  // Composer
  // ---------------------------------------------------------------------------
  onInputChange() {
    const hasText = this.inputTarget.value.trim().length > 0
    this.sendBtnTarget.disabled = !hasText && !(this.hasFilesTarget && this.filesTarget.files?.length)
    this._autoGrow()
    this._sendTyping(true)
  }

  onInputKey(event) {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault()
      this.formTarget.requestSubmit()
    }
  }

  submitForm(event) {
    event.preventDefault()
    const text = this.inputTarget.value.trim()
    const files = this.hasFilesTarget ? (this.filesTarget.files || []) : []
    if (!text && files.length === 0) return

    // Generate a correlation id so the cable echo can replace any optimistic
    // placeholder this client inserted. Also lets us dedup if the broadcast
    // arrives before/after the POST response in any order.
    const clientUuid = `local-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`
    if (this.hasClientUuidInputTarget) this.clientUuidInputTarget.value = clientUuid

    const fd = new FormData(this.formTarget)
    fetch(this.formTarget.action, {
      method: "POST",
      body: fd,
      headers: { "X-CSRF-Token": csrfToken() },
      credentials: "same-origin",
    }).then((res) => {
      if (!res.ok) console.error("[chat] send failed", res.status)
      this._sendTyping(false)
    })

    this.inputTarget.value = ""
    if (this.hasFilesTarget) this.filesTarget.value = ""
    if (this.hasClientUuidInputTarget) this.clientUuidInputTarget.value = ""
    this.sendBtnTarget.disabled = true
    this._autoGrow()
  }

  _sendTyping(isTyping) {
    const now = Date.now()
    if (isTyping && now - this._lastTypingSent < 1500) return
    this._lastTypingSent = now
    this._subscription?.perform("typing", { is_typing: isTyping })
    if (isTyping) {
      clearTimeout(this._typingStopTimer)
      this._typingStopTimer = setTimeout(() => this._sendTyping(false), 3000)
    }
  }

  _autoGrow() {
    const el = this.inputTarget
    el.style.height = "auto"
    el.style.height = Math.min(el.scrollHeight, 140) + "px"
  }

  _bindAutoGrow() {
    this._autoGrow()
  }

  _scrollToBottom(smooth) {
    requestAnimationFrame(() => {
      const list = this.messagesTarget
      if (!list) return
      list.scrollTo({ top: list.scrollHeight, behavior: smooth ? "smooth" : "auto" })
    })
  }

  _markVisibleAsRead() {
    const items = this.messagesTarget.querySelectorAll("[data-message-id]")
    const last = items[items.length - 1]
    if (last) this._markMessageRead(Number(last.dataset.messageId))
  }

  _markMessageRead(id) {
    fetch(`/intervenant/messages/${id}/read`, {
      method: "POST",
      credentials: "same-origin",
      headers: { "X-CSRF-Token": csrfToken(), Accept: "application/json" },
    }).catch(() => {})
  }
}

function csrfToken() {
  return document.querySelector('meta[name="csrf-token"]')?.content || ""
}
